// src/components/viewer/Correspondence.jsx
//
// Two-way correspondence thread on the requester Viewer page.
// Reads the merged conversation (approvals.amazon.com correspondence + our own
// portal comments) and lets the request's OWN requester post a reply that syncs
// back to approvals.amazon.com — so requesters no longer juggle between this page
// and approvals.amazon.com.
//
// Posting is gated to the requester on the BACKEND (403 otherwise); the UI also
// hides/disables the reply box for non-requesters as a courtesy.

import React, { useState, useEffect, useCallback } from 'react';
import { getCorrespondence, addCorrespondence } from '../../services/api';

const fmtTime = (ts) => {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString();
  } catch (e) {
    return ts;
  }
};

const Correspondence = ({ requestId, userAlias, approvalId }) => {
  const [comments, setComments] = useState([]);
  const [requesterAlias, setRequesterAlias] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(null);

  const load = useCallback(async () => {
    if (!requestId) return;
    try {
      const data = await getCorrespondence(requestId);
      setComments(data.comments || []);
      setRequesterAlias((data.requesterAlias || '').toLowerCase());
      setLoadError(null);
    } catch (err) {
      setLoadError(err.message || 'Could not load correspondence');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    load();
  }, [load]);

  // Only the request's own requester may post (matches the backend gate).
  const canPost = !!userAlias && !!requesterAlias &&
    userAlias.toLowerCase() === requesterAlias;

  const handlePost = async () => {
    const message = draft.trim();
    if (!message || posting) return;
    setPosting(true);
    setPostError(null);

    // Optimistic append so the reply shows immediately.
    const optimistic = {
      id: `local-${Date.now()}`,
      alias: userAlias,
      message,
      timestamp: new Date().toISOString(),
      source: 'approver_portal',
      _pending: true,
    };
    setComments((prev) => [...prev, optimistic]);
    setDraft('');

    try {
      await addCorrespondence(requestId, userAlias, message);
      // Re-fetch to get the authoritative thread (and drop the optimistic marker).
      await load();
    } catch (err) {
      // Roll back the optimistic entry and surface the error (e.g. the 403 gate).
      setComments((prev) => prev.filter((c) => c.id !== optimistic.id));
      setDraft(message);
      setPostError(err.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  // If the request never went to approvals (no approvalId) and has no comments,
  // there's nothing to show — keep the viewer uncluttered.
  if (!loading && !loadError && comments.length === 0 && !approvalId) {
    return null;
  }

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '10px',
      padding: '20px',
      marginTop: '12px',
    }}>
      <div style={{ fontSize: '16px', fontWeight: '800', color: '#111', marginBottom: '4px' }}>
        💬 Correspondence
      </div>
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
        Comments here sync with approvals.amazon.com — no need to switch tabs.
      </div>

      {loading ? (
        <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
          Loading conversation…
        </div>
      ) : loadError ? (
        <div style={{
          fontSize: '13px', color: '#b91c1c', background: '#fef2f2',
          border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px',
        }}>
          {loadError}
        </div>
      ) : comments.length === 0 ? (
        <div style={{ fontSize: '13px', color: '#94a3b8', fontStyle: 'italic', marginBottom: '4px' }}>
          No comments yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {comments.map((c) => {
            const mine = userAlias && (c.alias || '').toLowerCase() === userAlias.toLowerCase();
            const fromApprovals = c.source === 'amazon_approvals';
            return (
              <div
                key={c.id}
                style={{
                  alignSelf: mine ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  background: mine ? '#eef6ff' : '#f8fafc',
                  border: `1px solid ${mine ? '#bfdbfe' : '#e2e8f0'}`,
                  borderRadius: '10px',
                  padding: '10px 14px',
                  opacity: c._pending ? 0.6 : 1,
                }}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  marginBottom: '4px',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#334155' }}>
                    {c.alias || 'unknown'}
                  </span>
                  <span style={{
                    fontSize: '10px', fontWeight: '600', padding: '1px 6px',
                    borderRadius: '8px',
                    background: fromApprovals ? '#fef3c7' : '#dcfce7',
                    color: fromApprovals ? '#92400e' : '#166534',
                  }}>
                    {fromApprovals ? 'Approvals' : 'Portal'}
                  </span>
                  <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: 'auto' }}>
                    {c._pending ? 'sending…' : fmtTime(c.timestamp)}
                  </span>
                </div>
                <div style={{ fontSize: '14px', color: '#111', whiteSpace: 'pre-wrap' }}>
                  {c.message}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reply box — only for the request's own requester */}
      {!loading && canPost && (
        <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid #eef2f7' }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add a comment…"
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px',
              borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px',
              fontFamily: 'inherit', resize: 'vertical',
            }}
          />
          {postError && (
            <div style={{ fontSize: '12px', color: '#b91c1c', marginTop: '6px' }}>
              {postError}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button
              onClick={handlePost}
              disabled={posting || !draft.trim()}
              style={{
                padding: '8px 18px', borderRadius: '8px', border: 'none',
                background: (posting || !draft.trim()) ? '#cbd5e1' : '#0073bb',
                color: '#fff', fontSize: '13px', fontWeight: '600',
                cursor: (posting || !draft.trim()) ? 'default' : 'pointer',
              }}
            >
              {posting ? 'Posting…' : 'Post Comment'}
            </button>
          </div>
        </div>
      )}

      {/* Non-requester note (only when there IS a thread to view) */}
      {!loading && !canPost && comments.length > 0 && (
        <div style={{ marginTop: '14px', fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
          Only the requester of this request can post comments.
        </div>
      )}
    </div>
  );
};

export default Correspondence;
